<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Popup } from '@progress/kendo-vue-popup'
import { RadioGroup, type RadioGroupChangeEvent } from '@progress/kendo-vue-inputs';

import { ref, onMounted } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { GetOrderTypeId, GetImageRootUrl } from '../../stores/SettingsStore'
import { FormatCurrency } from '../../services/currencyFormatter'
import type { IShippingAddress } from '../../types/CartTypes.js'
import AddressControl from '../PWS/AddressControl.vue'
import VerifyAddressPopup from '../VerifyAddressPopup.vue'
import { ShippingAddressAddToCart, ShipToTypeAddToCart, ShippingMethodAddToCart } from '../../services/CartService'
import { handleError } from '../../services/ErrorService'

import type { IAddressVerificationListItem, IAddress } from '../../types/CountryCultureTypes'
import type { IAoeCart } from '../../types/CartTypes'

const logger = useLogger()

// Address control ref for validation
const addressControlRef = ref<InstanceType<typeof AddressControl> | null>(null)

const verifyAddress = ref<IAddressVerificationListItem[]>([])
const addressVerificationShow = ref<boolean>(false)
const addressVerifyError = ref<boolean>(false)
const showNewAddress = ref<boolean>(false)
const hideNewAddress = ref<boolean>(false)
const shippingAddressFirstName = ref<string>('')
const shippingAddressLastName = ref<string>('')
const shippingAddressLine1 = ref<string>('')
const shippingAddressLine2 = ref<string | null>('')
const shippingAddressHouseNumber = ref<string | null>('')
const shippingAddressCity = ref<string>('')
const shippingAddressStateCode = ref<string | null>('')
const shippingAddressPostalCode = ref<string>('')
const shippingAddressCountryCode = ref<string>('')
const orderTypeId = ref<string>('')
const imageRootURL = ref<string>('')
const shipToTypeSelected = ref<string>('')
const shipTypes = ref<iShipToTypes[]>([])
const shipMethodSelected = ref<string>('')
const shipMethods = ref<iShipToTypes[]>([])

const emit = defineEmits(["update-cart"]);

const props = defineProps<{
  cart: IAoeCart | null
  selectedcountry: string
  showshiptotype: boolean
  disabled?: boolean
}>()

interface iShipToTypes {
  label: string
  value: string
}

onMounted(async () => {
  logger.debug('onMounted')
  orderTypeId.value = await GetOrderTypeId()
  imageRootURL.value = await GetImageRootUrl()
})

function addressControlUpdateHandler(data: IAddress)
{
  logger.debug('addressControlUpdateHandler', data)

  shippingAddressFirstName.value = data.firstName || ''
  shippingAddressLastName.value = data.lastName || ''
  shippingAddressLine1.value = data.addressLine1 || ''
  shippingAddressLine2.value = data.addressLine2 || ''
  shippingAddressHouseNumber.value = data.houseNumber || ''
  shippingAddressCity.value = data.city || ''
  shippingAddressStateCode.value = data.stateCode || ''
  shippingAddressPostalCode.value = data.postalCode || ''
  shippingAddressCountryCode.value = data.countryCode || ''
}

function addShippingAddress(event: MouseEvent)
{
  logger.debug('addShippingAddressToCart', event, shippingAddressLine1.value, shippingAddressLine2.value, shippingAddressHouseNumber.value, shippingAddressCity.value, shippingAddressStateCode.value, shippingAddressPostalCode.value, shippingAddressCountryCode.value)

  // Validate address before saving
  if (addressControlRef.value && !addressControlRef.value.validate()) {
    return // Don't proceed if address validation fails
  }

  addShippingAddressToCart(false, event);
}

async function addShippingAddressToCart(skipAddressValidation: boolean, event: MouseEvent)
{
  addressVerificationShow.value = false;
  verifyAddress.value = []
  logger.debug('addShippingAddressToCart')
  const shippingAddress: IShippingAddress = {
        firstName: shippingAddressFirstName.value,
        lastName: shippingAddressLastName.value,
        addressLine1: shippingAddressLine1.value,
        addressLine2: shippingAddressLine2.value,
        houseNumber: shippingAddressHouseNumber.value,
        city: shippingAddressCity.value,
        stateCode: shippingAddressStateCode.value,
        postalCode: shippingAddressPostalCode.value,
        countryCode: shippingAddressCountryCode.value
      }

      //Call Cart Address Add
      logger.debug('ShippingAddressAddToCart')
      try {
        const response = await ShippingAddressAddToCart({
          aoeCartIdToken: props.cart? props.cart.aoeCartIdToken : '',
          address: shippingAddress,
          skipAddressValidation: skipAddressValidation,
          updateAddressBook: true
        })

        logger.debug('ShippingAddressAddToCart response:', response)
        for (const item of response.items) {
          verifyAddress.value.push(item);
        }

        if (response.item == null)
        {
          addressVerifyError.value = true
        }
        else
        {
          addressVerifyError.value = false
        }
      } catch (error) {
        logger.error('ShippingAddressAddToCart error:', error)
        await handleError(error, t)
        // Still emit update-cart so parent can clear loading state and refresh
        emit("update-cart", event)
        return
      }

    if (verifyAddress.value.length == 0 && addressVerifyError.value == false) {
      emit("update-cart", event)
      showNewAddress.value = false
    }
    else
    {
      addressVerificationShow.value = true;
    }
}


function useEnteredAddressClick(event: MouseEvent) {
  logger.debug('useEnteredAddressClick')

  addShippingAddressToCart(true, event)
}

function useSelectedAddressClick(selectedAddress: IAddress, event: MouseEvent) {
  logger.debug('useSelectedAddressClick', selectedAddress)

  shippingAddressFirstName.value = selectedAddress.firstName || ''
  shippingAddressLastName.value = selectedAddress.lastName || ''
  shippingAddressLine1.value = selectedAddress.addressLine1 || ''
  shippingAddressLine2.value = selectedAddress.addressLine2 || ''
  shippingAddressHouseNumber.value = selectedAddress.houseNumber || ''
  shippingAddressCity.value = selectedAddress.city || ''
  shippingAddressStateCode.value = selectedAddress.stateCode || ''
  shippingAddressPostalCode.value = selectedAddress.postalCode || ''
  shippingAddressCountryCode.value = selectedAddress.countryCode || ''

  addShippingAddressToCart(false, event)
}

function radioBtnChangeNameHandler(event: RadioGroupChangeEvent)
{
  logger.debug('radioBtnChangeNameHandler', event)
  shipToTypeSelected.value = event.value

  // Reset showNewAddress and set hideNewAddress when changing ship types
  showNewAddress.value = false
  if (event.value !== 'PARTY' && event.value !== 'PARTY2') {
    hideNewAddress.value = true
  }
  else
  {
    hideNewAddress.value = false
  }

  ShipToTypeAddToCart(
    {
      aoeCartIdToken: props.cart ? props.cart.aoeCartIdToken : '',
      codeIdShipToType: event.value
    }
  ).then(() => {
    logger.debug('ShipToTypeAddToCart success')
    emit("update-cart", event)
  })
}

function GetShipTypes() : iShipToTypes[]
{
  shipTypes.value = []
  if (props.cart?.availableShipToTypes != null)
  {
    for (const shipType of props.cart?.availableShipToTypes) {
      shipTypes.value.push({
        label: shipType.shipToTypeName,
        value: shipType.codeId
      })
    }
  }
  return shipTypes.value
}

function GetSelectedShipType() : string
{
  if (shipTypes.value.length > 0)
  {
    if (shipToTypeSelected.value != '')
    {
      return shipToTypeSelected.value
    }
    if (props.cart != null && props.cart.codeIdShipToType.codeId != null && props.cart.codeIdShipToType.codeId != '')
    {
      return props.cart.codeIdShipToType.codeId
    }
  }
  return ''
}

function radioBtnShippingMethodHandler(event: RadioGroupChangeEvent)
{
  logger.debug('radioBtnChangeNameHandler', event)
  shipMethodSelected.value = event.value

  ShippingMethodAddToCart(
    {
      aoeCartIdToken: props.cart ? props.cart.aoeCartIdToken : '',
      shippingMethodName: event.value
    }
  ).then(() => {
    logger.debug('ShipMethodAddToCart success')
    emit("update-cart", event)
  })
}

function formatCurrency(value : number) {
  // Use the centralized FormatCurrency function from currencyFormatter.ts
  // Pass the currencyId from the cart, or null if not available
  const currencyId = props.cart?.currencyId || null
  return FormatCurrency(value, currencyId)
}

function GetShipMethods() : iShipToTypes[]
{
  shipMethods.value = []
  if (props.cart != null && props.cart.availableShippingMethods != null)
  {
    for (const shipType of props.cart.availableShippingMethods) {
      shipMethods.value.push({
        label: shipType.shippingMethodFriendlyName + ' - ' + formatCurrency(shipType.shippingCost),
        value: shipType.shippingMethodName
      })
    }
  }
  return shipMethods.value
}

function GetSelectedShipMethod() : string
{
  if (shipMethods.value.length > 0)
  {
    if (shipMethodSelected.value != '')
    {
      return shipMethodSelected.value
    }
    if (props.cart != null && props.cart.shippingMethod && props.cart.shippingMethod.name != null && props.cart.shippingMethod.name != '')
    {
      return props.cart.shippingMethod.name
    }
  }

  return ''
}

function validate(): boolean {
  // Validate AddressControl fields if address form is visible
  if (showNewAddress.value || (props.cart == null || props.cart.shippingAddress == null || props.cart.shippingAddress == undefined)) {
    if (addressControlRef.value && !addressControlRef.value.validate()) {
      return false // Don't proceed if address validation fails
    }
  }
  return true
}

defineExpose({ validate })
</script>
<template>
  <div v-if="props.cart != null && props.cart.aoeCartIdToken != ''" class="aoe-shipping-control">
    <div class="row party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div shipping-section-header">
        {{ t('partyOrderEntry.ShippingSectionHeader') }}
      </div>
    </div>
    <div class="row">
      <div v-if="props.showshiptotype" class="col-12 col-md-4 ship-to-type-section">
        <div class="row mt-3">
          <div class="col-12 shipping-section-title">
            {{ t('partyOrderEntry.ShipToTypesTitle') }}:
          </div>
        </div>
        <div class="row mt-3 ship-to-type-row">
          <div class="col-12 ship-to-type-div">
            <RadioGroup
              @change="radioBtnChangeNameHandler($event)"
              class="ship-to-type-radio"
              :data-items="GetShipTypes()"
              :value="GetSelectedShipType()"
              :disabled="props.disabled"
            />
          </div>
        </div>
      </div>
      <div class="col-12 col-md-8">
        <div class="row mt-3">
          <div class="col-12 shipping-section-title">
            {{ t('partyOrderEntry.ShippingAddressTitle') }}:
          </div>
        </div>
        <div v-if="props.cart == null || props.cart.shippingAddress == null || props.cart.shippingAddress == undefined || showNewAddress">
          <div class="row">
            <div class="col-12 address-control-div">
              <AddressControl
                ref="addressControlRef"
                :countryCode="selectedcountry"
                :addressLine1="null"
                :addressLine2="null"
                :houseNumber="null"
                :city="null"
                :stateCode="null"
                :postalCode="null"
                :isdisabled="props.disabled ?? false"
                :columnClass="'col-12 col-md-3'"
                :showfullnamefield="true"
                :name="null"
                @address-change="addressControlUpdateHandler">
              </AddressControl>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 address-control-save-div">
              <KButton
                @click="addShippingAddress($event)"
                :disabled="props.disabled"
                class="baseBtn primaryBtn save-address-button"
                :rounded="'large'">
                {{ t('partyOrderEntry.SaveAddressButton') }}
              </KButton>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="row">
            <div class="col-12">
              <div v-if="props.cart.shippingAddress.countryCode == 'US' || props.cart.shippingAddress.countryCode == 'CA'" class="row mt-3">
                <div class="col-12 shipping-address">
                  {{ props.cart.shippingAddress.firstName }}&nbsp;{{ props.cart.shippingAddress.lastName }}
                </div>
                <div class="col-12 shipping-address">
                  {{ props.cart.shippingAddress.addressLine1 }}
                </div>
                <div class="col-12 shipping-address">
                  {{ props.cart.shippingAddress.addressLine2 }}
                </div>
                <div class="col-12 shipping-address">
                  {{ props.cart.shippingAddress.city }}<template v-if="props.cart.shippingAddress.stateCode && props.cart.shippingAddress.stateCode !== 'N/A'">,&nbsp;{{ props.cart.shippingAddress.stateCode }}</template>&nbsp;{{ props.cart.shippingAddress.postalCode }}
                </div>
              </div>
              <div v-if="props.cart.shippingAddress.countryCode == 'AT' || props.cart.shippingAddress.countryCode == 'DE' || props.cart.shippingAddress.countryCode == 'FR'" class="row mt-3">
                <div class="col-12 shipping-address">
                  {{ props.cart.shippingAddress.firstName }}&nbsp;{{ props.cart.shippingAddress.lastName }}
                </div>
                <div class="col-12 shipping-address">
                  {{ props.cart.shippingAddress.addressLine1 }} {{ props.cart.shippingAddress.houseNumber }}
                </div>
                <div class="col-12 shipping-address">
                  {{ props.cart.shippingAddress.addressLine2 }}
                </div>
                <div class="col-12 shipping-address">
                  {{ props.cart.shippingAddress.postalCode }}&nbsp;{{ props.cart.shippingAddress.city }},&nbsp;{{ props.cart.shippingAddress.countryCode }}
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12">
              <KButton
                v-if="GetSelectedShipType() != 'PARTY' && GetSelectedShipType() != 'PARTY2'"
                @click="showNewAddress = true"
                :disabled="props.disabled"
                class="baseBtn secondaryBtn add-new-address-button"
                :rounded="'large'">
                {{ t('partyOrderEntry.AddNewAddress') }}
              </KButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row mt-3 party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div shipping-method-section-header">
        {{ t('partyOrderEntry.ShippingMethodsSectionHeader') }}
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-md-4 ship-method-type-section">
        <div class="row mt-3">
          <div class="col-12 shipping-section-title">
            {{ t('partyOrderEntry.ShipMethodTypesTitle') }}:
          </div>
        </div>
        <div class="row mt-3 ship-method-type-row">
          <div class="col-12 ship-method-type-div">
            <RadioGroup
              @change="radioBtnShippingMethodHandler($event)"
              class="ship-method-radio"
              :data-items="GetShipMethods()"
              :value="GetSelectedShipMethod()"
              :disabled="props.disabled"
            />
          </div>
        </div>
      </div>
    </div>


    <!-- Gray overlay background for address verification popup -->
    <Teleport to="body">
      <div v-if="addressVerificationShow" class="aoe-popup-overlay" @click="addressVerificationShow = false"></div>
    </Teleport>

    <Popup
      :show="addressVerificationShow"
      :popup-class="'address-val-popup-content'"
    >
    <VerifyAddressPopup
        :enteredAddress="{
          addressLine1: shippingAddressLine1,
          addressLine2: shippingAddressLine2,
          houseNumber: shippingAddressHouseNumber,
          city: shippingAddressCity,
          stateCode: shippingAddressStateCode,
          postalCode: shippingAddressPostalCode,
          countryCode: shippingAddressCountryCode,
          firstName: null,
          lastName: null,
        }"
        :verifyAddress="verifyAddress"
        :verifyAddressError="addressVerifyError"
        @use-entered-address="useEnteredAddressClick"
        @use-selected-address="useSelectedAddressClick"
        @re-enter-address="addressVerificationShow = false">
      </VerifyAddressPopup>
    </Popup>
  </div>
</template>

<style lang="scss">

.ship-to-type-section
{
  border-right: 1px solid var(--section-header-color);
}

.ship-to-type-div input,
.ship-method-type-div input
{
  margin: $spacing-2-5;
}

.ship-method-div label,
.ship-to-type-div label,
.ship-method-type-div label
{
  @include typography-small;
}

.aoe-popup-overlay
{
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(black, 0.5);
  z-index: $z-dropdown !important;
  overflow: hidden;
}

</style>
